import React, { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { DataContext } from "../../../../newComponents/context/DataContext";
import {
  FaArrowLeft,
  FaInstagram,
  FaBuilding,
  FaFileAlt,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaTag,
  FaGift,
  FaTicketAlt,
  FaRupeeSign,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaUsers,
  FaUserCircle,
} from "react-icons/fa";
import { MdVerified, MdBusiness } from "react-icons/md";
import LoadingScreen from "../../../../newComponents/utilities/LoadingScreen";

const apiDomain = process.env.REACT_APP_API_DOMAIN;

const InfoRow = ({ label, value, icon: Icon }) => (
  <div className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
    {Icon && <Icon className="text-blue-500 mt-1 flex-shrink-0" size={15} />}
    <div className="min-w-0">
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
      <p className="text-sm text-gray-800 font-medium break-all">{value || "N/A"}</p>
    </div>
  </div>
);

const Badge = ({ text, color = "blue" }) => {
  const colors = {
    blue: "bg-blue-100 text-blue-700",
    green: "bg-green-100 text-green-700",
    yellow: "bg-yellow-100 text-yellow-700",
    red: "bg-red-100 text-red-700",
    purple: "bg-purple-100 text-purple-700",
  };
  return (
    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${colors[color]}`}>
      {text}
    </span>
  );
};

const SponsorProfileView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const passedSponsor = location.state?.sponsor;

  const { eventData, allEventsSummary, filteredSponsor } = useContext(DataContext);

  const [sponsorData, setSponsorData] = useState(passedSponsor || null);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [serverEvents, setServerEvents] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [userInfo, setUserInfo] = useState(null); // /api/v2/userinformation (registration data)

  // Pull canonical registration info (email/mobile/dob/address) — the sponsor
  // list payload doesn't carry these reliably, so the previous version of
  // this page showed "N/A" everywhere for accounts whose IG side had not
  // been hydrated.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const numericId = String(userId).includes(":")
      ? String(userId).split(":")[1]
      : String(userId);
    (async () => {
      try {
        const res = await axios.get(`${apiDomain}/api/v2/userinformation`, {
          params: { userID: numericId, t: Date.now() },
        });
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [res.data];
        const u = list?.[0];
        if (u && (u.email || u.mobile_no || u.city || u.state || u.name)) {
          setUserInfo(u);
        }
      } catch (_) {
        // No info on file — leave userInfo null
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Hydrate from cached sponsor list when navigating directly via URL — keeps
  // the header populated instead of dumping "No sponsor data found".
  useEffect(() => {
    if (sponsorData) return;
    const list = Array.isArray(filteredSponsor)
      ? filteredSponsor
      : filteredSponsor?.sponsors || [];
    const match = list.find(
      (s) => String(s.user_id) === String(userId) || String(s.facebookId) === String(userId)
    );
    if (match) setSponsorData(match);
  }, [filteredSponsor, sponsorData, userId]);

  useEffect(() => {
    if (!userId) return;
    const fetchProfile = async () => {
      setLoadingProfile(true);
      try {
        const res = await axios.get(`${apiDomain}/api/v2/sponsor/profile/get`, {
          params: { sponsorId: userId },
        });
        setBusinessProfile(res.data);
      } catch (e) {
        console.error("Error fetching sponsor profile:", e);
        setBusinessProfile(null);
      } finally {
        setLoadingProfile(false);
      }
    };
    fetchProfile();
  }, [userId]);

  // Always fetch authoritative sponsored-events list from the backend so the
  // detail page works even when navigated directly via URL (no location state).
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const fetchEvents = async () => {
      setLoadingEvents(true);
      try {
        const res = await axios.get(`${apiDomain}/api/v2/geteventbysponsor`, {
          params: { id: userId, page: 1, pageSize: 100 },
        });
        if (cancelled) return;
        const evs = res.data?.sponsorData?.events || res.data?.events || [];
        setServerEvents(Array.isArray(evs) ? evs : []);
      } catch (e) {
        console.error("Error fetching sponsor events:", e);
        if (!cancelled) setServerEvents([]);
      } finally {
        if (!cancelled) setLoadingEvents(false);
      }
    };
    fetchEvents();
    return () => { cancelled = true; };
  }, [userId]);

  // Pull transactions where this sponsor is the user. Best-effort — the
  // endpoint indexes by numeric user id, so we strip the "facebook:" prefix
  // before querying.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const numericId = String(userId).includes(":")
      ? String(userId).split(":")[1]
      : String(userId);
    (async () => {
      try {
        const res = await axios.get(`${apiDomain}/api/v2/filteredtransactions`, {
          params: { user_id: numericId, pageSize: 100 },
        });
        if (cancelled) return;
        const list =
          res.data?.transactions ||
          res.data?.data ||
          (Array.isArray(res.data) ? res.data : []);
        setTransactions(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) setTransactions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (!sponsorData) {
    // Show a graceful placeholder until either location.state or filteredSponsor
    // hydrates. If neither does within the load, we still render the page with
    // whatever the backend returned.
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        <p className="text-gray-500 text-sm">Loading sponsor…</p>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
        >
          <FaArrowLeft /> Go Back
        </button>
      </div>
    );
  }

  const ig = sponsorData.ig_user || {};

  // Prefer server-fetched events; fall back to the eventCategories embedded in
  // sponsorData (from the list endpoint).
  const sponsoredEvents = (() => {
    if (serverEvents.length > 0) {
      return serverEvents.map((ev) => ({
        eventId: String(ev.eventID || ev.event_id || ""),
        eventName: ev.eventName || ev.event_name || `Event #${ev.eventID || ev.event_id}`,
        startDate: ev.startDate || ev.start_date,
        endDate: ev.endDate || ev.end_date,
        location: ev.city || ev.location || "",
        categories: Array.isArray(ev.sponsorData) ? ev.sponsorData : [],
      }));
    }
    return Object.entries(sponsorData.eventCategories || {}).map(
      ([eventId, categories]) => {
        const eventDetail =
          eventData?.find((e) => String(e.eventID) === String(eventId)) ||
          allEventsSummary?.find((e) => String(e.event_id) === String(eventId));
        return {
          eventId,
          eventName:
            eventDetail?.eventName || eventDetail?.event_name || `Event #${eventId}`,
          startDate: eventDetail?.startDate || eventDetail?.start_date,
          endDate: eventDetail?.endDate || eventDetail?.end_date,
          location: eventDetail?.location || eventDetail?.city || "",
          categories: Array.isArray(categories) ? categories : [],
        };
      }
    );
  })();

  const sponsorshipTxns = transactions.filter((t) => {
    const remarks = t.remarks || t.Remarks || {};
    const paymentFor = (remarks.payment_for || remarks.paymentFor || "").toString().toLowerCase();
    return paymentFor.includes("sponsor") || t.type === "debit";
  });

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "business", label: "Business Info" },
    { id: "events", label: `Sponsored Events (${sponsoredEvents.length})` },
    { id: "transactions", label: `Transactions (${sponsorshipTxns.length})` },
  ];

  return (
    <main className="lg:mr-8 pb-16">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors mb-6 group"
      >
        <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
        <span className="font-medium">Back to Sponsors</span>
      </button>

      {/* Hero Profile Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        {/* Cover */}
        <div className="h-28 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />

        <div className="px-6 pb-6">
          {/* Avatar */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between -mt-14 mb-4 gap-4">
            <div className="flex items-end gap-4">
              {ig.profile_picture_url ? (
                <img
                  src={ig.profile_picture_url}
                  alt={ig.name}
                  className="w-24 h-24 rounded-full border-4 border-white shadow-lg object-cover"
                />
              ) : (
                <div className="w-24 h-24 rounded-full border-4 border-white shadow-lg bg-gray-200 flex items-center justify-center">
                  <FaUserCircle size={56} className="text-gray-400" />
                </div>
              )}
              <div className="mb-1">
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-gray-900">
                    {ig.name || userInfo?.name || businessProfile?.organizationName || "—"}
                  </h1>
                  <MdVerified className="text-blue-500" size={20} />
                </div>
                <p className="text-gray-500 flex items-center gap-1">
                  <FaInstagram className="text-pink-500" />
                  {ig.username ? (
                    <a
                      href={`https://instagram.com/${ig.username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      @{ig.username}
                    </a>
                  ) : (
                    <span>not linked</span>
                  )}
                </p>
                {(userInfo?.email || userInfo?.mobile_no) && (
                  <p className="text-gray-500 text-sm mt-1">
                    {userInfo?.email}
                    {userInfo?.email && userInfo?.mobile_no ? " · " : ""}
                    {userInfo?.mobile_no}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Badge text={`${sponsoredEvents.length} Events Sponsored`} color="purple" />
              {ig.followers_count > 0 && (
                <Badge text={`${ig.followers_count?.toLocaleString()} Followers`} color="blue" />
              )}
            </div>
          </div>

          {/* IG Bio */}
          {ig.biography && (
            <p className="text-gray-600 text-sm mb-4 bg-gray-50 rounded-lg p-3 italic">
              "{ig.biography}"
            </p>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-4">
            {[
              { label: "Followers", value: ig.followers_count?.toLocaleString() || "0" },
              { label: "Following", value: ig.follows_count?.toLocaleString() || "0" },
              { label: "Posts", value: ig.media_count?.toLocaleString() || "0" },
              { label: "Events", value: sponsoredEvents.length },
              {
                label: "Total Spend",
                value:
                  "₹" +
                  (sponsoredEvents.reduce((sum, ev) =>
                    sum + ev.categories.reduce((s, c) => s + (Number(c.price) || 0), 0), 0
                  ).toLocaleString()),
              },
            ].map((stat) => (
              <div key={stat.label} className="text-center bg-gray-50 rounded-xl p-3">
                <p className="text-lg font-bold text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? "bg-white text-blue-600 shadow-sm"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {activeTab === "overview" && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Instagram Info */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <FaInstagram className="text-pink-500" size={18} />
              <h2 className="text-lg font-semibold text-gray-800">Instagram Profile</h2>
            </div>
            <InfoRow label="Full Name" value={ig.name} />
            <InfoRow label="Username" value={`@${ig.username}`} />
            <InfoRow label="Followers" value={ig.followers_count?.toLocaleString()} />
            <InfoRow label="Following" value={ig.follows_count?.toLocaleString()} />
            <InfoRow label="Total Posts" value={ig.media_count?.toLocaleString()} />
            {ig.website && (
              <div className="py-2 border-b border-gray-100">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Website</p>
                <a
                  href={ig.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-500 hover:underline break-all"
                >
                  {ig.website}
                </a>
              </div>
            )}
            <InfoRow label="IG Account ID" value={ig.id} />
          </div>

          {/* Sponsor App Info */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <FaUsers className="text-blue-500" size={18} />
              <h2 className="text-lg font-semibold text-gray-800">Sponsor App Info</h2>
            </div>
            <InfoRow label="User ID" value={sponsorData.user_id} />
            <InfoRow label="Events Sponsored" value={sponsorData.eventsSponsored || sponsoredEvents.length} />
            <InfoRow
              label="States Active"
              value={sponsorData.states?.join(", ") || "N/A"}
              icon={FaMapMarkerAlt}
            />
            <InfoRow
              label="Cities Active"
              value={sponsorData.cities?.join(", ") || "N/A"}
              icon={FaMapMarkerAlt}
            />
            <InfoRow
              label="Pincodes"
              value={sponsorData.pincodes?.join(", ") || "N/A"}
            />
          </div>

          {/* Registration / Account Info */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <FaBuilding className="text-emerald-500" size={18} />
              <h2 className="text-lg font-semibold text-gray-800">Registration / Account</h2>
            </div>
            {userInfo ? (
              <div className="grid md:grid-cols-2 gap-x-6">
                <InfoRow label="Full Name" value={userInfo.name} />
                <InfoRow label="Email" value={userInfo.email} />
                <InfoRow label="Mobile" value={userInfo.mobile_no} />
                <InfoRow label="Date of Birth" value={userInfo.dob} />
                <InfoRow label="Gender" value={userInfo.gender} />
                <InfoRow label="Age" value={userInfo.age || undefined} />
                <InfoRow
                  label="Address"
                  value={
                    [
                      userInfo.building_no,
                      userInfo.home_street,
                      userInfo.city,
                      userInfo.state,
                      userInfo.pin_code,
                    ]
                      .filter((v) => v && String(v).trim() !== "")
                      .join(", ")
                  }
                  icon={FaMapMarkerAlt}
                />
                <InfoRow label="Referral Code" value={userInfo.referral_code} />
                <InfoRow label="Active" value={userInfo.is_active ? "Yes" : "No"} />
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">
                No registration info on file for this account.
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── BUSINESS INFO TAB ── */}
      {activeTab === "business" && (
        <div className="grid md:grid-cols-2 gap-6">
          {loadingProfile ? (
            <div className="md:col-span-2 flex items-center justify-center py-16">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-3" />
                <p className="text-gray-500">Loading business profile…</p>
              </div>
            </div>
          ) : !businessProfile || Object.keys(businessProfile).length === 0 ? (
            <div className="md:col-span-2 text-center py-16 text-gray-400">
              <MdBusiness size={48} className="mx-auto mb-3 opacity-40" />
              <p className="text-lg">No business profile found for this sponsor.</p>
            </div>
          ) : (
            <>
              {/* Organization Details */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-4">
                  <MdBusiness className="text-blue-500" size={18} />
                  <h2 className="text-lg font-semibold text-gray-800">Organization Details</h2>
                </div>
                <InfoRow label="Organization Name" value={businessProfile.organizationName} icon={FaBuilding} />
                <InfoRow label="GST Number" value={businessProfile.gstNumber} />
                <InfoRow label="Contact Person" value={businessProfile.contactPersonName} />
                <InfoRow label="Contact Email" value={businessProfile.contactEmail} />
                <InfoRow label="Contact Phone" value={businessProfile.contactPhone} />
                <InfoRow label="Business Address" value={businessProfile.businessAddress} icon={FaMapMarkerAlt} />
                <InfoRow label="City" value={businessProfile.city} />
                <InfoRow label="State" value={businessProfile.state} />
                <InfoRow label="Pincode" value={businessProfile.pincode} />
              </div>

              {/* Identity Proofs */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-4">
                  <FaFileAlt className="text-green-500" size={18} />
                  <h2 className="text-lg font-semibold text-gray-800">Identity Documents</h2>
                </div>

                {/* Logo */}
                {businessProfile.logoUrl && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">Organization Logo</p>
                    <img
                      src={businessProfile.logoUrl}
                      alt="Organization Logo"
                      className="h-20 w-auto rounded-lg border border-gray-200 shadow-sm object-contain bg-gray-50 p-1"
                    />
                  </div>
                )}

                {/* Identity Proof 1 */}
                <div className="mb-4">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">
                    {businessProfile.identityProof1Label || "Identity Proof 1"}
                  </p>
                  {businessProfile.identityProof1Url ? (
                    <a
                      href={businessProfile.identityProof1Url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded-lg transition-colors"
                    >
                      <FaFileAlt size={14} /> View Document
                    </a>
                  ) : (
                    <span className="text-sm text-gray-400 flex items-center gap-1">
                      <FaTimesCircle className="text-red-300" size={14} /> Not uploaded
                    </span>
                  )}
                </div>

                {/* Identity Proof 2 */}
                <div className="mb-4">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">
                    {businessProfile.identityProof2Label || "Identity Proof 2"}
                  </p>
                  {businessProfile.identityProof2Url ? (
                    <a
                      href={businessProfile.identityProof2Url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded-lg transition-colors"
                    >
                      <FaFileAlt size={14} /> View Document
                    </a>
                  ) : (
                    <span className="text-sm text-gray-400 flex items-center gap-1">
                      <FaTimesCircle className="text-red-300" size={14} /> Not uploaded
                    </span>
                  )}
                </div>

                {/* Approval Status */}
                {businessProfile.approvalStatus !== undefined && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">Approval Status</p>
                    {businessProfile.approvalStatus === "approved" ? (
                      <span className="inline-flex items-center gap-2 text-green-700 bg-green-50 px-3 py-1 rounded-full text-sm font-medium">
                        <FaCheckCircle size={13} /> Approved
                      </span>
                    ) : businessProfile.approvalStatus === "rejected" ? (
                      <span className="inline-flex items-center gap-2 text-red-700 bg-red-50 px-3 py-1 rounded-full text-sm font-medium">
                        <FaTimesCircle size={13} /> Rejected
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2 text-yellow-700 bg-yellow-50 px-3 py-1 rounded-full text-sm font-medium">
                        <FaClock size={13} /> Pending
                      </span>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── SPONSORED EVENTS TAB ── */}
      {activeTab === "events" && (
        <>
          {loadingEvents && sponsoredEvents.length === 0 ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-3" />
              <p className="text-gray-500">Loading sponsored events…</p>
            </div>
          ) : sponsoredEvents.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <FaCalendarAlt size={48} className="mx-auto mb-3 opacity-40" />
              <p className="text-lg">No sponsored events found.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {sponsoredEvents.map((ev) => (
                <div
                  key={ev.eventId}
                  className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                >
                  {/* Event Header */}
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-5 py-4">
                    <Link
                      to={`/event-form/${ev.eventId}`}
                      className="text-white font-bold text-lg leading-tight hover:underline inline-flex items-center gap-2"
                      title="Open event edit page"
                    >
                      <span>{ev.eventName}</span>
                      <span className="text-xs bg-white/20 px-1.5 py-0.5 rounded">#{ev.eventId}</span>
                    </Link>
                    <div className="flex flex-wrap gap-3 mt-2 text-indigo-100 text-sm">
                      {ev.startDate && (
                        <span className="flex items-center gap-1">
                          <FaCalendarAlt size={12} />
                          {new Date(ev.startDate).toLocaleDateString("en-GB")}
                          {ev.endDate && ` – ${new Date(ev.endDate).toLocaleDateString("en-GB")}`}
                        </span>
                      )}
                      {ev.location && (
                        <span className="flex items-center gap-1">
                          <FaMapMarkerAlt size={12} /> {ev.location}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Packages / Categories */}
                  <div className="p-4 space-y-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Sponsorship Packages ({ev.categories.length})
                    </p>
                    {ev.categories.map((cat, idx) => (
                      <div
                        key={idx}
                        className="border border-gray-200 rounded-xl p-3 bg-gray-50 hover:bg-white transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <FaTag className="text-indigo-400" size={13} />
                            <span className="font-semibold text-gray-800 text-sm">
                              {cat.categoryName || `Package ${idx + 1}`}
                            </span>
                          </div>
                          <span className="flex items-center gap-1 text-sm font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                            <FaRupeeSign size={10} />
                            {Number(cat.price || 0).toLocaleString()}
                          </span>
                        </div>

                        {cat.description && (
                          <p className="text-xs text-gray-500 mb-2 pl-5">{cat.description}</p>
                        )}

                        <div className="grid grid-cols-2 gap-2 pl-5">
                          <div className="flex items-center gap-2 bg-white rounded-lg px-2 py-1.5 border border-gray-100">
                            <FaTicketAlt className="text-blue-400" size={12} />
                            <div>
                              <p className="text-xs text-gray-400">Coupons</p>
                              <p className="text-xs font-semibold text-gray-700">
                                {cat.noOfRemainingCoupons ?? "—"} / {cat.noOfCoupons ?? "—"}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 bg-white rounded-lg px-2 py-1.5 border border-gray-100">
                            <FaGift className="text-pink-400" size={12} />
                            <div>
                              <p className="text-xs text-gray-400">Gift Hampers</p>
                              <p className="text-xs font-semibold text-gray-700">
                                {cat.noOfRemainingGiftHampers ?? "—"} / {cat.noOfGiftHampers ?? "—"}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Event Total */}
                    <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                      <span className="text-xs text-gray-500 font-medium">Total Spent on Event</span>
                      <span className="text-sm font-bold text-indigo-700">
                        ₹{ev.categories.reduce((s, c) => s + (Number(c.price) || 0), 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Grand Total */}
          {sponsoredEvents.length > 0 && (
            <div className="mt-6 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-5 text-white flex items-center justify-between">
              <div>
                <p className="text-indigo-200 text-sm">Total Investment</p>
                <p className="text-3xl font-extrabold">
                  ₹{sponsoredEvents
                    .reduce((sum, ev) =>
                      sum + ev.categories.reduce((s, c) => s + (Number(c.price) || 0), 0), 0
                    )
                    .toLocaleString()}
                </p>
              </div>
              <div className="text-right">
                <p className="text-indigo-200 text-sm">Across</p>
                <p className="text-3xl font-extrabold">{sponsoredEvents.length} Events</p>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── TRANSACTIONS TAB ── */}
      {activeTab === "transactions" && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          {sponsorshipTxns.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <FaRupeeSign size={40} className="mx-auto mb-3 opacity-40" />
              <p className="text-lg">No transactions recorded for this sponsor.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Date</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Reference</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Type</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Event</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-700">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sponsorshipTxns.map((t, idx) => {
                    const remarks = t.remarks || t.Remarks || {};
                    const evRef = remarks.event_id || remarks.eventId || "";
                    const evName = sponsoredEvents.find((e) => String(e.eventId) === String(evRef))?.eventName;
                    const status = (t.status || "").toLowerCase();
                    const statusClass =
                      status === "success"
                        ? "bg-green-100 text-green-700"
                        : status === "failed"
                        ? "bg-red-100 text-red-700"
                        : "bg-yellow-100 text-yellow-700";
                    const ts =
                      t.timestamp || t.created_at || t.createdAt || t.CreatedAt || "";
                    return (
                      <tr key={`${t.tran_id || t.tranID || idx}-${idx}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-600">
                          {ts
                            ? new Date(ts).toLocaleString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "—"}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">
                          {t.tran_id || t.tranID || t.id || "—"}
                        </td>
                        <td className="px-4 py-3 capitalize">{t.type || "—"}</td>
                        <td className="px-4 py-3">
                          {evRef ? (
                            <Link
                              to={`/event-form/${evRef}`}
                              className="text-blue-600 hover:underline"
                              title="Open event edit page"
                            >
                              {evName || `Event #${evRef}`}
                            </Link>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusClass}`}>
                            {t.status || "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-gray-800">
                          ₹{Number(t.amount || 0).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </main>
  );
};

export default SponsorProfileView;
